import {
  DeviceEnabledType,
  enumerable,
  IoTVEntitySequentialProgrammeDef,
  NodeEdgeMapper,
  objectsMatch,
  RollingPatternReport,
  UserAttributeDefs,
} from '@iotv/datamodel-core'
import { Milliseconds, Position, ValidBusinessObject } from '@iotv/iotv-v3-types'
import { ErrorType, FullReported, IrrigatorStatusByteType, PulseCountByOctantInput } from '../../types'
import { IrrigatorProps, SprinklerCoverage, SprinklerType } from './types'
import { updateEntity } from './updateEntity'

const ERROR_EXCLUDE_LIST: string[] = ['runawayError', 'linkCheckErr']
const typeName = 'Irrigator'
const OFFLINETHRESHOLDMS = Milliseconds.DAY
export interface ITimeIngestStats {
  retryCount: number
  snr?: number
  rssi?: number
  sf?: number
  time: number
}

export enum IrrStatusTypes {
  ONLINE = 'Online - not irrigating',
  OFFLINE = 'Offline',
  ERROR = 'Error',
  IRRIGATING = 'Irrigating',
  UNSEEN = 'Never Seen',
  DISABLED = 'Disabled',
  AREAPAUSED = 'Area Paused',
  AWAITINGPROGRAMME = 'Programme Pending',
}

export const irrStatusTypes = [
  { name: 'Online - not irrigating', id: IrrStatusTypes.ONLINE },
  { name: 'Offline', id: IrrStatusTypes.OFFLINE },
  { name: 'Disabled', id: IrrStatusTypes.DISABLED },
  { name: 'Area Paused', id: IrrStatusTypes.AREAPAUSED },
  { name: 'Error', id: IrrStatusTypes.ERROR },
  { name: 'Irrigating', id: IrrStatusTypes.IRRIGATING },
  { name: 'Never Seen', id: IrrStatusTypes.UNSEEN },
  { name: 'Programme Pending', id: IrrStatusTypes.AWAITINGPROGRAMME },
]

const parseErrors = (errors: any) => {
  const foundErrors: string[] = []
  if (errors) {
    // const data = JSON.parse(errors);
    const data = errors
    for (let key in data) {
      let val = data[key]
      if (val === 1 && !!!ERROR_EXCLUDE_LIST.includes(key)) {
        foundErrors.push(key)
      }
    }
  }
  if (foundErrors.length === 0) {
    return undefined
  } else {
    return foundErrors
  }
}

export class Irrigator extends DeviceEnabledType implements IrrigatorProps {
  constructor(ob: any) {
    super(ob.type || typeName, ob)
    this.updateFromDeviceMessage = updateEntity.bind(this)
    this.calcStatus = ob ? this.getStatus(ob) : undefined
    this.timestamp = ob.timestamp ?? Date.now()
    this.ingestion = ob.ingestion
    this.pause = ob.pause || false
    this.incomingEuis = ob.incomingEuis
    // this.solenoidModel = ob.solenoidModel
    this.solenoidVoltage = ob.solenoidVoltage
    this.solenoidVoltageEUI = ob.solenoidVoltageEUI
    this.desiredVoltage = ob.desiredVoltage

    // if (ob.deviceEuis && ob.deviceEuis.length === 1) {
    //   this.deviceEui = ob.deviceEuis[0]
    // }
  }

  timestamp: number

  @enumerable(false)
  updateFromDeviceMessage: (payload: Partial<FullReported>) => void

  reportedScheduleEqualsRequested(): boolean | undefined {
    let equal: boolean | undefined = undefined
    const requested = this.lastRequestedConfig
    const reported = this.config
    if (requested && reported) {
      //both defined
      requested.forEach((requestedSchedule, i) => {
        const reportedScehdule = reported[i]
        if (reportedScehdule) {
          const match = objectsMatch(requestedSchedule, reportedScehdule) //
          if (!match) console.log(`reported and requested schedules dont match on item ${i}`, { requestedSchedule, reportedScehdule })
          equal = !!((equal || equal === undefined) && match) // dont reset once false
        } else equal = false
      })
    } else if (reported || requested) {
      // one is defined but not the other
      equal = false
    } else {
      // neither defined, so we will return undefined
    }
    return equal
  }

  // battery implemented by inherited deviceBatteryVoltage

  // requested config populated by CalculatedState from DeviceState when message contains config
  lastRequestedConfig: IoTVEntitySequentialProgrammeDef[] | undefined // SolenoidControllerScheduleConfigResponse[] | undefined
  // reported config updated by CalculatedState from incoming device message when message contains config
  config: IoTVEntitySequentialProgrammeDef[] | undefined // SolenoidControllerScheduleConfigResponse[] | undefined
  rollingPatternConfig: RollingPatternReport | undefined
  // from device message metadata
  customerDeviceId?: string
  deviceEui?: string
  // From DeviceNWAttributes
  sf?: number | undefined
  rssi?: number | undefined
  snr?: number | undefined
  gatewayEui?: string | undefined

  // from V020 payload
  sprinklerApplicationRate?: number | undefined
  model?: SprinklerType | undefined
  sprinklerCoverage?: SprinklerCoverage | undefined

  firmwareVersion?: number
  margin?: number
  moisture?: number
  octants?: PulseCountByOctantInput
  sysTime?: number
  temperature?: number
  error?: ErrorType
  status?: IrrigatorStatusByteType
  edit?: boolean
  pause?: boolean
  incomingProgramme?: string
  currentProgramme?: string
  ingestion?: ITimeIngestStats[]

  travellingCurrentLocation?: Position
  fixedThrowArea?: number
  travellingThrowArea?: number
  applicationPotential?: number
  hasDevice?: boolean
  isOperating?: boolean
  updated_at?: number

  // solenoidModel?: SolenoidTypes
  solenoidVoltage?: number
  solenoidVoltageEUI?: string
  desiredVoltage?: number

  get schedulesMatch() {
    return this.reportedScheduleEqualsRequested()
  }

  getStatus = (irrigator: ValidBusinessObject = this, mapper: NodeEdgeMapper<ValidBusinessObject> | undefined = this.nodeEdgeMapper) => {
    const status: IrrStatusTypes[] = []
    const now = new Date()
    const lastSeen = new Date(irrigator.updated_at)
    if (isNaN(lastSeen?.getTime())) {
      status.push(IrrStatusTypes.UNSEEN)
      // status.push(StatusTypes.OFFLINE);
    }
    if (lastSeen?.getTime() < now.getTime() - OFFLINETHRESHOLDMS) {
      status.push(IrrStatusTypes.OFFLINE)
    }
    if (irrigator.pause) {
      status.push(IrrStatusTypes.AREAPAUSED)
    }
    if (irrigator.disabled) {
      status.push(IrrStatusTypes.DISABLED)
    }
    if (parseErrors(irrigator.error)) {
      status.push(IrrStatusTypes.ERROR)
    }
    if (irrigator.isOperating) {
      status.push(IrrStatusTypes.IRRIGATING)
    }

    const parentPaddock = mapper && (mapper.getAdjacentNodes(irrigator, { objectTypeId: 'Area' })[0] as any)
    if (parentPaddock?.pause === true) {
      status.push(IrrStatusTypes.AREAPAUSED)
    }
    if (irrigator.incomingProgramme !== undefined && !(status.includes(IrrStatusTypes.DISABLED) || status.includes(IrrStatusTypes.AREAPAUSED))) {
      status.push(IrrStatusTypes.AWAITINGPROGRAMME)
    }

    if (status.length === 0) {
      status.push(IrrStatusTypes.ONLINE)
    }

    return status
  }

  getUserAttributes(): UserAttributeDefs {
    return {
      ...super.getUserAttributes(),
      name: {
        type: 'String',
        editable: true,
        label: 'Name',
        name: 'name',
        description: 'Name of sprinkler',
        required: true,
      },
      sprinklerApplicationRate: {
        type: 'Number',
        editable: true,
        label: 'Sprinkler Rate (mm/hr)',
        name: 'Sprinkler Rate (mm/hr)',
        // required: true,
      },
      model: {
        type: 'Enum',
        enumKv: SprinklerType,
        editable: true,
        label: 'Sprinkler Model',
        name: 'Sprinkler Model',
      },
      sprinklerCoverage: {
        type: 'Enum',
        enumKv: SprinklerCoverage,
        editable: true,
        label: 'Sprinkler Coverage',
        name: 'Sprinkler Coverage',
      },
      // solenoidModel: {
      //   type: 'Enum',
      //   enumKv: getEnumShape(SolenoidOptions),
      //   editable: true,
      //   label: 'Solenoid Model',
      //   name: 'Solenoid Model',
      // },
      // irrigatorType: {
      //   type: 'Enum',
      //   enumKv: IrrigatorType,
      //   editable: true,
      //   label: 'Type',
      //   name: 'type',
      //   description: 'Sprinkler model type (Solid State, Pivot, Lateral, Traveling, K-Line, Gun)',
      // },
      comments: {
        type: 'String',
        editable: true,
        label: 'Comments',
        name: 'comments',
        description: 'Free text for info ',
      },
    }
  }
}
